"use client";

// Add MCP connection modal — Figma: OTEN-AIS_In-review nodes 7897-107469 (base),
// 7901-110138 / 7995-124840 (OAuth credentials), 7902-112350 (URL error),
// 7901-111185 (admin approval + guided steps). Jira AIS-1835 (flows 6A–6E).
//
// Probe-on-URL UX: as soon as a valid HTTP(S) URL is typed, the server is
// probed (debounced) and the auth requirements are detected. Servers that need
// static OAuth credentials reveal the yellow "OAuth — credentials required"
// section (Callback URL + Client ID/Secret). Users can instead connect directly
// with custom headers or no auth; every mode verifies discovery before closing.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui";
import { InlineStatus } from "@/components/ui/inline-status";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { domId } from "@/lib/dom-id";
import { DUPLICATE_ERROR, isDuplicateError } from "@/lib/duplicate-errors";
import { HttpError } from "@/lib/fetcher";
import { useMcpConnect } from "@/hooks/use-mcp-connect";
import { useMcpUrlCheck } from "@/hooks/use-mcp-url-check";
import UnsavedChangesModal from "@/components/unsaved-changes-modal";
import { McpConnectionFormFields } from "@/components/mcp/mcp-connection-form-fields";
import { McpSetupInstruction } from "@/components/mcp/mcp-setup-instruction";
import {
  createMcp,
  deleteMcp,
  switchMcpStatus,
  updateMcp,
} from "@/services/mcps-service";
import type { MCPConfig } from "@/services/schemas/mcp-schemas";
import {
  createAuthConfig,
  getAuthConfig,
  updateAuthConfig,
} from "@/services/auth-config-service";
import {
  probeMCP,
  resolveMcpInstruction,
  MCPConnectError,
  MCPProbeError,
} from "@/services/mcp-service";
import { mcpConnectErrorMessage } from "@/lib/mcp-connect-error";
import { type CustomHeader } from "@/components/mcp/custom-headers-input";
import {
  authEndpointsFromProbe,
  buildMcpAuthConfig,
  deriveAuthType,
  isValidHttpUrl,
  isValidHttpsUrl,
  probeNeedsAdminApproval,
  probeNeedsCredentials,
  type DerivedAuthType,
  type McpConnectionMode,
} from "@/lib/mcp-auth-derive";
import {
  CONNECTION_NAME_MAX_LENGTH,
  isConnectionNameWithinLimit,
  isValidConnectionName,
} from "@/lib/connection-name-validation";

const FIXED_TRANSPORT = "streamable_http" as const;

// AC (story-055): Connector Name auto-filled from URL when blank.
// "https://github.mcp.com/x" → "Github MCP".
function suggestNameFromUrl(value: string): string {
  try {
    const host = new URL(value).hostname.replace(/^www\./, "");
    const label = host.split(".")[0] ?? "";
    if (!label) return "";
    return `${label.charAt(0).toUpperCase()}${label.slice(1)} MCP`;
  } catch {
    return "";
  }
}

export interface AddMcpModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new mcp_id after a fully-connected save. */
  onCreated: (mcpId: string) => void;
}

export default function AddMcpModal({
  open,
  onClose,
  onCreated,
}: AddMcpModalProps) {
  const t = useTranslations("Mcp.addModal");
  const tMcp = useTranslations("Mcp");
  const purpose = "mcp-add-modal";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");

  // Submit-scoped error — rendered above the footer.
  const [formError, setFormError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const nameRef = useRef(name);
  nameRef.current = name;

  // Best-effort per-app setup instruction — a doc/guide URL, shown as a link
  // when the server needs manual credentials. null = none/not-yet-resolved.
  const [instructionUrl, setInstructionUrl] = useState<string | null>(null);

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [connectionMode, setConnectionMode] = useState<McpConnectionMode>("auto");
  const deactivationRef = useRef<Promise<unknown> | null>(null);
  const [activating, setActivating] = useState(false);

  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<CustomHeader[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [createdMcpId, setCreatedMcpId] = useState<string | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const {
    waitingForOauth,
    setWaitingForOauth,
    verifyingDiscovery,
    disposeOAuth,
    connectAndFinish,
  } = useMcpConnect();
  const busy = submitting || waitingForOauth || verifyingDiscovery || activating;

  // AC (story-055): a blank Connector Name is auto-filled once the URL probes.
  const fillNameFromUrl = useCallback((probed: string) => {
    if (nameRef.current.trim()) return;
    setName(suggestNameFromUrl(probed));
    setNameError(null);
  }, []);
  // Server URL check (format + probe); its error is rendered red under the
  // Server URL field per design d04.
  const {
    probe: urlProbe,
    probing,
    urlError,
    checkFailed,
    setUrlError,
    mapProbeError,
    handleUrlChange: changeUrl,
    onUrlFocusChange,
    reset: resetUrlCheck,
  } = useMcpUrlCheck({
    url,
    setUrl,
    active: open,
    probeEnabled: connectionMode === "auto",
    onProbed: fillNameFromUrl,
  });

  // Reset everything whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setCreatedMcpId(null);
    setName("");
    setDescription("");
    setUrl("");
    resetUrlCheck();
    setFormError(null);
    setNameError(null);
    setClientId("");
    setClientSecret("");
    setConnectionMode("auto");
    deactivationRef.current = null;
    setActivating(false);
    setAdvanceOpen(false);
    setCustomHeaders([]);
    setSubmitting(false);
    setShowDiscardModal(false);
    setWaitingForOauth(false);
  }, [open, resetUrlCheck, setWaitingForOauth]);

  const needsCreds = urlProbe ? probeNeedsCredentials(urlProbe) : false;
  const adminApproval = urlProbe ? probeNeedsAdminApproval(urlProbe) : false;
  const showOauthSection = connectionMode === "auto" && (needsCreds || adminApproval);

  // Resolve the setup instruction (a doc link) as soon as a valid HTTP(S) URL
  // settles — debounced and independent of the probe, so it appears right after
  // the user finishes typing the server URL. Best-effort: failures / no-match
  // leave instructionUrl null and never block the flow.
  useEffect(() => {
    if (!open) return;
    const trimmed = url.trim();
    if (!trimmed || !isValidHttpUrl(trimmed)) {
      setInstructionUrl(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      resolveMcpInstruction(trimmed).then((r) => {
        if (!cancelled)
          setInstructionUrl(r.matched ? r.instruction ?? null : null);
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, url]);

  // Probe detected an OAuth server needing static credentials → auto-expand
  // Advance Settings so Client ID / Secret are immediately visible (feedback
  // 2026-06-12: the credential fields live inside Advance Settings).
  useEffect(() => {
    if (showOauthSection || connectionMode === "direct") setAdvanceOpen(true);
  }, [showOauthSection, connectionMode]);

  const canConnect = useMemo(() => {
    if (busy || (connectionMode === "auto" && probing)) return false;
    if (!name.trim()) return false;
    if (!isValidConnectionName(name)) return false;
    // A name error on screen (e.g. "too long") blocks Connect until the name
    // is changed.
    if (nameError) return false;
    if (!isValidHttpUrl(url.trim())) return false;
    // Wait for the check of the current URL; one that failed can be retried.
    if (connectionMode === "auto" && !urlProbe && !checkFailed) return false;
    // Design d02/d03: Connect stays disabled until both credentials are filled.
    if (showOauthSection && (!clientId.trim() || !clientSecret.trim())) {
      return false;
    }
    return true;
  }, [busy, probing, connectionMode, name, nameError, url, urlProbe, checkFailed, showOauthSection, clientId, clientSecret]);

  const hasInput =
    Boolean(name.trim() || description.trim() || url.trim()) ||
    Boolean(clientId.trim() || clientSecret.trim()) ||
    customHeaders.some((h) => h.name.trim() || h.value.trim());

  // Like Add New Connector: closing with typed input asks before discarding it.
  const handleClose = useCallback(() => {
    if (busy && !waitingForOauth) return;
    if (hasInput) {
      setShowDiscardModal(true);
      return;
    }
    disposeOAuth();
    onClose();
  }, [busy, disposeOAuth, hasInput, onClose, waitingForOauth]);

  // Discard also drops the server a failed or unfinished attempt created, as
  // Add New Connector drops its draft connector. Best-effort: a role that may
  // not delete MCP servers keeps it.
  const handleDiscard = useCallback(() => {
    setShowDiscardModal(false);
    disposeOAuth();
    if (createdMcpId) void deleteMcp(createdMcpId).catch(() => undefined);
    onClose();
  }, [createdMcpId, disposeOAuth, onClose]);

  const handleUrlChange = useCallback(
    (nextUrl: string) => {
      changeUrl(nextUrl);
      // The setup instruction belongs to the previous URL.
      setInstructionUrl(null);
    },
    [changeUrl],
  );

  const handleConnectionModeChange = useCallback(
    (mode: McpConnectionMode) => {
      setConnectionMode(mode);
      setUrlError(null);
    },
    [setUrlError],
  );

  const handleNameChange = useCallback((nextName: string) => {
    setName(nextName);
    // An error describes the name it was raised for.
    setNameError(null);
  }, []);

  const showNameTooLongError = useCallback(() => {
    setNameError(t("errors.nameTooLong", { max: CONNECTION_NAME_MAX_LENGTH }));
  }, [t]);

  // Persist then verify.
  const handleConnect = useCallback(async () => {
    setFormError(null);
    setUrlError(null);

    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    const trimmedDescription = description.trim();
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();

    if (!trimmedName) {
      setNameError(t("errors.nameRequired"));
      return;
    }
    if (!isConnectionNameWithinLimit(trimmedName)) {
      showNameTooLongError();
      return;
    }
    if (!isValidConnectionName(trimmedName)) {
      setNameError(t("errors.nameInvalid"));
      return;
    }
    if (!isValidHttpUrl(trimmedUrl)) {
      setUrlError(t("errors.urlInvalid"));
      return;
    }

    if (!canConnect) return;
    setSubmitting(true);
    let savedMcpId = "";
    let derivedAuth: DerivedAuthType = "none";
    try {
      // Reuse the live probe when it matches the URL; re-probe otherwise so a
      // stale detection can never leak into the persisted auth config.
      const liveProbe = connectionMode === "direct"
        ? null
        : urlProbe ?? (await probeMCP(trimmedUrl));
      const endpoints = authEndpointsFromProbe(liveProbe);
      derivedAuth = deriveAuthType(
        liveProbe,
        trimmedClientId,
        connectionMode,
      );

      if (derivedAuth !== "none") {
        if (
          !isValidHttpsUrl(endpoints.authzUrl) ||
          !isValidHttpsUrl(endpoints.tokenUrl)
        ) {
          setFormError(tMcp("add.errors.discoveryFailed"));
          setSubmitting(false);
          return;
        }
      }
      if (
        derivedAuth === "oauth2" &&
        !trimmedClientId &&
        !endpoints.regEndpoint
      ) {
        setFormError(tMcp("add.errors.clientIdRequired"));
        setSubmitting(false);
        return;
      }

      const cleanedHeaders = customHeaders
        .filter((h) => h.name.trim() !== "")
        .map((h) => ({
          name: h.name.trim(),
          value: h.value,
          is_secret: h.is_secret,
        }));

      const mcpConfig: MCPConfig = {
        transport: FIXED_TRANSPORT,
        endpoint_url: trimmedUrl,
      };
      if (cleanedHeaders.length > 0) {
        mcpConfig.custom_headers = cleanedHeaders;
      }

      // A retry after a cancelled or failed authorization replaces the server
      // the failed attempt created, so the workspace never holds two copies.
      // A role that may create but not delete MCP servers gets a 403 on the
      // delete; that retry updates the failed attempt's server in place.
      let reuseMcpId: string | null = null;
      if (createdMcpId) {
        await deactivationRef.current;
        deactivationRef.current = null;
        try {
          await deleteMcp(createdMcpId);
        } catch (error) {
          if (!(error instanceof HttpError)) throw error;
          if (error.status === 403) reuseMcpId = createdMcpId;
          else if (error.status !== 404) throw error;
        }
        if (!reuseMcpId) setCreatedMcpId(null);
      }
      const payload = {
        name: trimmedName,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        config: mcpConfig,
      };
      const saved = reuseMcpId
        ? await updateMcp(reuseMcpId, payload)
        : await createMcp(payload);
      savedMcpId = saved.mcp_id;
      setCreatedMcpId(savedMcpId);
      // A reused server keeps the inactive status the failed attempt set.
      const reactivateOnSuccess = saved.status === "inactive";

      const authConfigBody = buildMcpAuthConfig(
        savedMcpId,
        derivedAuth,
        endpoints,
        trimmedClientId,
        trimmedClientSecret,
      );

      let authConfigExists = false;
      try {
        const existing = await getAuthConfig(savedMcpId);
        authConfigExists =
          existing.status === "OK" &&
          Array.isArray(existing.data) &&
          existing.data.length > 0;
      } catch {
        // 404 / no config — create below.
      }
      const savedAuth = authConfigExists
        ? await updateAuthConfig(savedMcpId, authConfigBody)
        : await createAuthConfig(authConfigBody);
      if (savedAuth.status !== "OK") {
        throw new MCPConnectError(savedAuth.error_code ?? "", savedAuth.message);
      }

      await connectAndFinish({
        mcpId: savedMcpId,
        authType: derivedAuth,
        discoverAfter: true,
        verifyDiscovery: true,
        onSuccess: () => {
          if (!reactivateOnSuccess) {
            onCreated(savedMcpId);
            return;
          }
          setActivating(true);
          void switchMcpStatus(savedMcpId, "active")
            .catch(() => {
              // Best-effort only — the server is connected either way.
            })
            .finally(() => {
              setActivating(false);
              onCreated(savedMcpId);
            });
        },
        onFailure: (reason) => {
          if (reason.kind === "discovery") {
            setSubmitting(false);
            setFormError(t("errors.verificationFailed", {
              message: mcpConnectErrorMessage(reason.error, tMcp),
            }));
            return;
          }
          if (savedMcpId) {
            deactivationRef.current = switchMcpStatus(savedMcpId, "inactive").catch(() => {
              // Best-effort only — keep the original OAuth failure surfaced.
            });
          }
          setSubmitting(false);
          if (reason.kind === "error") {
            setFormError(reason.message);
          } else if (reason.kind === "cancel") {
            setFormError(t("errors.oauthCancelled"));
          } else {
            setFormError(tMcp("add.errors.popupBlocked"));
          }
        },
      });
    } catch (err) {
      if (savedMcpId && derivedAuth !== "none") {
        deactivationRef.current = switchMcpStatus(savedMcpId, "inactive").catch(() => {
          // Best-effort only — preserve the original save/connect error.
        });
      }
      console.error("Failed to add MCP connection:", err);
      if (err instanceof MCPProbeError) {
        setUrlError(mapProbeError(err));
      } else if (isDuplicateError(err, DUPLICATE_ERROR.mcpName)) {
        setFormError(t("errors.nameExists"));
      } else if (err instanceof MCPConnectError) {
        // Classified MCP_CONNECT_* / EXECUTOR_* code → localized message.
        setFormError(mcpConnectErrorMessage(err, tMcp));
      } else {
        setFormError(
          err instanceof Error && err.message
            ? err.message
            : t("errors.saveFailed"),
        );
      }
    } finally {
      if (!waitingForOauth) {
        setSubmitting(false);
      }
    }
  }, [
    clientId,
    clientSecret,
    createdMcpId,
    customHeaders,
    description,
    mapProbeError,
    name,
    onCreated,
    urlProbe,
    connectAndFinish,
    setUrlError,
    showNameTooLongError,
    t,
    tMcp,
    url,
    waitingForOauth,
    canConnect,
    connectionMode,
  ]);

  if (!open) return null;

  return (
    <>
      <Dialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose();
        }}
      >
        <DialogContent
          data-testid={domId("mdl", purpose)}
          showClose={false}
          className="flex max-h-[90vh] w-[calc(100vw-32px)] max-w-[1180px] flex-col gap-4 overflow-hidden rounded-2xl p-8"
          onPointerDownOutside={(event) => {
            event.preventDefault();
            handleClose();
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            handleClose();
          }}
        >
          <DialogTitle className="sr-only">{t("title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("subtitle")}</DialogDescription>
          {/* Title — design: 28px SemiBold + 12px gray subtitle */}
          <div className="flex flex-col">
            <h2 className="text-[28px] font-semibold leading-[1.4] text-black">
              {t("title")}
            </h2>
            <p className="text-xs leading-[1.4] text-[#4C6075]">{t("subtitle")}</p>
          </div>

          <form
            className="flex min-h-0 flex-1 flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              void handleConnect();
            }}
          >
            {/* Only the fields scroll; the footer stays put. The fields keep the
                form's 24px rhythm, and the 4px inset keeps focus rings unclipped. */}
            <div className="-m-1 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-1">
              <McpConnectionFormFields
                purpose={purpose}
                variant="add"
                disabled={busy}
                autoFocusName
                name={name}
                onNameChange={handleNameChange}
                nameMaxLength={CONNECTION_NAME_MAX_LENGTH}
                nameError={nameError}
                onNameLimitExceeded={showNameTooLongError}
                description={description}
                onDescriptionChange={setDescription}
                url={url}
                onUrlChange={handleUrlChange}
                onUrlClear={() => handleUrlChange("")}
                onUrlFocusChange={onUrlFocusChange}
                urlError={urlError}
                probing={connectionMode === "auto" && probing}
                connectionMode={connectionMode}
                onConnectionModeChange={handleConnectionModeChange}
                advanceOpen={advanceOpen}
                onAdvanceOpenChange={setAdvanceOpen}
                headers={customHeaders}
                onHeadersChange={setCustomHeaders}
                showOauthSection={showOauthSection}
                credsRequired={showOauthSection}
                adminApproval={adminApproval}
                clientId={clientId}
                onClientIdChange={setClientId}
                clientSecret={clientSecret}
                onClientSecretChange={setClientSecret}
                beforeAdvanced={
                  connectionMode === "auto" && instructionUrl ? (
                    <McpSetupInstruction purpose={purpose} url={instructionUrl} />
                  ) : null
                }
              />

              {waitingForOauth && (
                <InlineStatus
                  purpose={`${purpose}-oauth-waiting`}
                  data-testid={domId("sec", `${purpose}-oauth-waiting`)}
                  status="info"
                >
                  {tMcp("add.oauthWaiting")}
                </InlineStatus>
              )}

              {formError && (
                <InlineStatus
                  purpose={`${purpose}-error`}
                  data-testid={domId("sec", `${purpose}-error`)}
                  status="error"
                  onDismiss={() => setFormError(null)}
                >
                  {formError}
                </InlineStatus>
              )}
            </div>

            {/* Footer — Cancel (text) | Connect (brand gradient) */}
            <div className="flex shrink-0 items-center justify-end gap-4">
              <Button
                data-testid={domId("btn", `${purpose}-cancel`)}
                type="button"
                variant="unstyled"
                onClick={handleClose}
                className="flex h-10 items-center justify-center rounded-lg px-5 text-sm font-medium leading-[1.4] text-[#00152C] transition hover:bg-gray-50 disabled:opacity-50"
                disabled={busy && !waitingForOauth}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                data-testid={domId("btn", `${purpose}-connect`)}
                type="submit"
                variant="unstyled"
                className="flex h-10 min-w-[168px] items-center justify-center gap-2 rounded-lg px-5 text-sm font-medium leading-[1.4] text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: "linear-gradient(90deg, #713D98 0%, #3E3D98 100%)",
                }}
                disabled={!canConnect}
              >
                {busy && (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                )}
                {busy ? t("actions.connecting") : t("actions.connect")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <UnsavedChangesModal
        isOpen={showDiscardModal}
        onKeepEditing={() => setShowDiscardModal(false)}
        onDiscard={handleDiscard}
      />
    </>
  );
}
